// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import RatingStars from '@/components/profile/RatingStars';
import SubmitRecommendationForm from '@/components/profile/SubmitRecommendationForm';

const CoachRecommendationResponse = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [recommendation, setRecommendation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [athleteName, setAthleteName] = useState<string>('');

  useEffect(() => {
    const fetchRecommendation = async () => {
      if (!token) {
        setError('No recommendation token provided');
        setLoading(false);
        return;
      }
      
      try {
        // Fetch the recommendation using the token
        const { data, error } = await supabase
          .from('coach_recommendations')
          .select(`
            *, 
            profiles:athlete_id (name)
          `)
          .eq('submission_token', token)
          .single();
          
        if (error) throw error;
        
        if (data) {
          setRecommendation(data);
          // Extract the athlete name from the joined profiles data
          if (data.profiles && data.profiles.name) {
            setAthleteName(data.profiles.name);
          }
        } else {
          setError('Recommendation not found');
        }
      } catch (err) {
        console.error('Error fetching recommendation:', err);
        setError('Error fetching recommendation details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchRecommendation();
  }, [token]);

  const handleSubmit = async (formData: any) => {
    if (!token || !recommendation) return;
    
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('coach_recommendations')
        .update({
          coach_name: formData.coach_name,
          coach_email: formData.coach_email,
          team_or_school: formData.team_or_school,
          coach_cell: formData.coach_cell,
          recommendation_text: formData.recommendation_text,
          athletic_ability_rating: formData.athletic_ability_rating,
          coachability_rating: formData.coachability_rating,
          attitude_rating: formData.attitude_rating,
          work_ethic_rating: formData.work_ethic_rating,
          teammate_rating: formData.teammate_rating,
          visible_to_athlete: true, // Make recommendations visible to athletes by default
          status: 'completed',
          submitted_at: new Date().toISOString()
        })
        .eq('submission_token', token);
        
      if (error) throw error;
      
      // Refresh recommendation data
      const { data: updatedData } = await supabase
        .from('coach_recommendations')
        .select('*')
        .eq('submission_token', token)
        .single();
        
      setRecommendation(updatedData);
      toast.success('Thank you! Your recommendation has been submitted.');
      
    } catch (err) {
      console.error('Error submitting recommendation:', err);
      toast.error('Failed to submit recommendation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="container mx-auto py-12 px-4 max-w-3xl">
        <Card>
          <CardContent className="p-12 flex flex-col items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-lg">Loading recommendation details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container mx-auto py-12 px-4 max-w-3xl">
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-600">Error Loading Recommendation</CardTitle>
            <CardDescription>
              We couldn't find a valid recommendation request with the provided token.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p>{error}</p>
            <p className="mt-4">
              If you believe this is an error, please contact the athlete who sent you the recommendation request.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // If recommendation was already submitted
  if (recommendation && recommendation.submitted_at) {
    return (
      <div className="container mx-auto py-12 px-4 max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Check className="h-6 w-6 text-green-500 mr-2" />
              Recommendation Already Submitted
            </CardTitle>
            <CardDescription>
              Thank you for submitting your recommendation for {athleteName || 'this athlete'}!
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="mb-4">Your recommendation has been recorded and will be visible to college coaches reviewing this athlete's profile.</p>
            <p className="text-muted-foreground">
              Submitted on: {recommendation.submitted_at ? new Date(recommendation.submitted_at).toLocaleDateString() : ''}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Show the recommendation submission form
  return (
    <div className="container mx-auto py-12 px-4 max-w-3xl">
      <Card>
        <CardHeader>
          <CardTitle>Coach Recommendation</CardTitle>
          <CardDescription>
            {athleteName 
              ? `Please provide your recommendation for ${athleteName}`
              : 'Please provide your recommendation for this athlete'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SubmitRecommendationForm
            initialData={recommendation}
            onSubmit={handleSubmit}
            isSubmitting={submitting}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default CoachRecommendationResponse;